// ---------------------------------------------------------------------------
// Sous-titres riches : l'ASS avec ses styles (JASSUB), l'image PGS (libpgs).
//
// Toji : « qu'on puisse vraiment lire tous les formats ». Le rendu maison
// (`soustitres.js`) peint du TEXTE : il lit des répliques VTT et les pose dans
// un calque réglable. Deux formats n'y entrent pas :
//   · l'ASS/SSA — une piste de fansub porte ses polices, ses positions, ses
//     couleurs, ses effets de karaoké ; converti en VTT par le serveur, il
//     garde les mots et perd tout le reste. JASSUB (libass en WebAssembly)
//     le rend tel que l'auteur l'a écrit ;
//   · le PGS — le format image des Blu-ray. Il n'y a AUCUN texte : ce sont des
//     bitmaps horodatés. libpgs les décode dans un Worker et les peint sur un
//     canvas. Sans lui, la piste n'existait tout simplement pas.
//
// ⚠️ CE MODULE EST PUR : ni `document`, ni `window`, ni bibliothèque au
// chargement. Les bibliothèques sont INJECTÉES (`chargerJassub`,
// `chargerLibpgs`) — en production, `lecteur.js` leur passe
// `() => import('jassub')` / `() => import('libpgs')`, qu'esbuild découpe en
// morceaux séparés (comme le démuxeur, voir build.mjs) : deux mégaoctets de
// wasm ne se paient qu'au moment où quelqu'un choisit une piste ASS. Le banc
// `essais/st-riches.mjs` leur substitue des factices.
//
// Les fichiers d'outillage (worker, wasm, police par défaut) sont servis par
// nous depuis `public/st/` (copiés/bundlés par build.mjs) : une bibliothèque
// bundlée ne peut pas les retrouver par `import.meta.url` — l'adresse du
// morceau esbuild ne mène nulle part.
// ---------------------------------------------------------------------------

/**
 * Ce que cette machine sait rendre, lu sur le global — jamais deviné.
 *
 * JASSUB exige WebAssembly, un Worker et un OffscreenCanvas (il transfère le
 * canvas au worker) ; libpgs n'exige qu'un Worker. `g` est injectable pour le
 * banc ; en production c'est `globalThis`.
 */
export function capacitesDuNavigateur(g = globalThis) {
  return {
    wasm: typeof g?.WebAssembly === 'object' && g.WebAssembly !== null && typeof g.WebAssembly.validate === 'function',
    worker: typeof g?.Worker === 'function',
    offscreen: typeof g?.OffscreenCanvas === 'function',
  };
}

/**
 * Le format dans lequel une piste sera RENDUE ici.
 *
 *   · `'ass'`   — JASSUB, quand wasm + worker (+ offscreen) sont là ;
 *   · `'image'` — libpgs, quand un worker est là ;
 *   · `'texte'` — le rendu maison sur le VTT du serveur : le cas général, et
 *                 le REPLI d'un ASS sur une machine sans WebAssembly (on perd
 *                 les styles, on garde les mots — c'est ce qu'on avait) ;
 *   · `null`    — une image sans worker : rien ne sait la peindre, et on ne
 *                 fait pas semblant (pas de piste vide dans le menu).
 *
 * `offscreen` n'est exigé que s'il est explicitement faux : un appelant qui
 * ne le renseigne pas (le contrat de base est `{ wasm, worker }`) n'est pas
 * pénalisé.
 */
export function formatDeRendu(piste, capacites) {
  const c = capacites ?? {};
  const f = piste?.format;
  if (f === 'ass') {
    return (c.wasm === true && c.worker === true && c.offscreen !== false) ? 'ass' : 'texte';
  }
  if (f === 'image') return c.worker === true ? 'image' : null;
  return 'texte';
}

/**
 * Les adresses d'une piste, prêtes à être données à une bibliothèque.
 *
 * ⚠️ `avecAcces` SUR LE CONTENU, JAMAIS SUR LES OUTILS. Une bibliothèque qui
 * fetch ne porte aucun en-tête : dans l'activité Discord, la session vient du
 * SDK et n'est pas en cookie — sans jeton dans l'adresse, `/api/subs/…`
 * répond 401 (même raison que `avecAcces` dans lecteur.js). Les outils de
 * `public/st/`, eux, sont publics.
 *
 * Le rang est celui que `-map 0:s:<rang>` attend côté serveur — image
 * comprises —, pas l'index du flux dans le conteneur.
 */
export function urlsDePiste({ ref, piste, base, avecAcces }) {
  const r = encodeURIComponent(String(ref ?? ''));
  const rang = Number(piste?.rang) || 0;
  const contenu = (chemin) => avecAcces(base(chemin));
  const f = piste?.format;
  if (f === 'image') return { sup: contenu(`/api/subs/${r}/${rang}.sup`) };
  if (f === 'ass') {
    return {
      ass: contenu(`/api/subs/${r}/${rang}.ass`),
      // Le VTT reste : c'est le repli quand JASSUB ne peut pas tourner ici.
      vtt: contenu(`/api/subs/${r}/${rang}.vtt`),
      polices: contenu(`/api/subs/${r}/polices`),
    };
  }
  return { vtt: contenu(`/api/subs/${r}/${rang}.vtt`) };
}

/** Une adresse par pièce jointe de police, telles que `/polices` les liste. */
export function urlsDesPolices({ ref, polices, base, avecAcces }) {
  const r = encodeURIComponent(String(ref ?? ''));
  return (Array.isArray(polices) ? polices : [])
    .filter((p) => p && Number.isInteger(Number(p.index)))
    .map((p) => avecAcces(base(`/api/subs/${r}/polices/${Number(p.index)}`)));
}

/** Les cinq fichiers de `public/st/`, sous `base` (le préfixe `/.proxy` de l'activité). */
export function urlsDesOutils(base) {
  return {
    workerJassub: base('/st/jassub-worker.js'),
    wasm: base('/st/jassub-worker.wasm'),
    wasmModerne: base('/st/jassub-worker-modern.wasm'),
    defaultWoff2: base('/st/default.woff2'),
    workerPgs: base('/st/libpgs.worker.js'),
  };
}

/**
 * Monte un rendu riche sur la vidéo, dans `conteneur`, et rend de quoi le
 * piloter : `{ pret, detruire(), montrer(), cacher(), decaler(s) }`.
 *
 * ⚠️ UN CANVAS À NOUS, PAS CELUI DE LA BIBLIOTHÈQUE. Sans `canvas`, JASSUB
 * colle le sien juste après `<video>` et libpgs fait pareil : ils se
 * retrouveraient sous le calque texte, sous le voile, hors de tout ce que la
 * page positionne. Le nôtre vit dans `#st-riches`, dont le CSS règle la
 * superposition.
 *
 * ⚠️ RIEN NE FUIT. Trois façons d'échouer, un seul nettoyage :
 *   · l'`import()` rejette (réseau, morceau absent) — aucune instance n'a
 *     existé, le canvas est retiré ;
 *   · `detruire()` arrive PENDANT l'import — l'instance n'est jamais
 *     construite (un worker et deux mégaoctets de wasm pour rien) ;
 *   · `ready` rejette (wasm refusé, .sup illisible) — l'instance est
 *     détruite, le canvas retiré.
 * Dans tous les cas `pret` rejette, et `destroy()`/`dispose()` n'est appelé
 * qu'UNE fois, quel que soit le nombre d'appels à `detruire()`.
 */
export function creerRenduRiche({
  format, video, conteneur, urls, polices = [], chargerJassub, chargerLibpgs, urlsOutils,
}) {
  const canvas = conteneur.ownerDocument.createElement('canvas');
  canvas.style.visibility = 'visible';
  conteneur.appendChild(canvas);

  let detruit = false;
  let instance = null;
  let liberer = null; // destroy()/dispose() de l'instance, une seule fois

  function detruire() {
    if (detruit) return;
    detruit = true;
    if (liberer) {
      const l = liberer;
      liberer = null;
      // `JASSUB.destroy()` est ASYNC et attend `ready` : quand le wasm a été
      // refusé, `ready` a rejeté et `destroy()` rejette aussi — sans lecteur,
      // c'était un rejet non géré dans la console (relevé du vérificateur).
      // Le worker est quand même terminé par la bibliothèque : rien à faire.
      try { Promise.resolve(l()).catch(() => {}); } catch { /* déjà libéré côté bibliothèque */ }
    }
    instance = null;
    canvas.remove();
  }

  const pret = (async () => {
    if (format === 'ass') {
      const mod = await chargerJassub();
      // `export default class JASSUB` en production ; un export nommé passe aussi.
      const JASSUB = mod?.default ?? mod?.JASSUB;
      if (typeof JASSUB !== 'function') throw new Error('JASSUB introuvable dans le module chargé');
      if (detruit) throw new Error('rendu détruit avant le chargement de JASSUB');
      instance = new JASSUB({
        video,
        canvas,
        subUrl: urls.ass,
        workerUrl: urlsOutils.workerJassub,
        wasmUrl: urlsOutils.wasm,
        modernWasmUrl: urlsOutils.wasmModerne,
        // Les polices du conteneur, préchargées : c'est ce que le fansub
        // attend, et c'est ce qui évite un texte qui change de police en cours
        // de réplique (voir « Understanding font management », README).
        fonts: [...polices],
        // La police par défaut servie par NOUS : sans cette entrée, JASSUB la
        // cherche par `import.meta.url` du morceau esbuild — introuvable.
        availableFonts: { 'liberation sans': urlsOutils.defaultWoff2 },
        defaultFont: 'liberation sans',
        // Les polices locales seulement : pas d'appel à Google Fonts depuis le
        // lecteur (`'localandremote'` chargerait 50 ko de plus et parlerait à
        // un tiers).
        queryFonts: 'local',
      });
      liberer = () => instance?.destroy?.();
      await instance.ready;
      if (detruit) throw new Error('rendu détruit pendant le démarrage de JASSUB');
      return;
    }
    if (format === 'image') {
      const mod = await chargerLibpgs();
      const PgsRenderer = mod?.PgsRenderer ?? mod?.default?.PgsRenderer ?? mod?.default;
      if (typeof PgsRenderer !== 'function') throw new Error('PgsRenderer introuvable dans le module chargé');
      if (detruit) throw new Error('rendu détruit avant le chargement de libpgs');
      instance = new PgsRenderer({
        workerUrl: urlsOutils.workerPgs,
        video,
        canvas,
        subUrl: urls.sup,
        // Doit suivre l'`object-fit` de la balise (`contain`, index.html) :
        // c'est ainsi que libpgs place l'image dans les bandes noires.
        aspectRatio: 'contain',
      });
      liberer = () => instance?.dispose?.();
      await instance.ready;
      if (detruit) throw new Error('rendu détruit pendant le démarrage de libpgs');
      return;
    }
    throw new Error(`format sans rendu riche : ${String(format)}`);
  })().catch((err) => {
    detruire();
    throw err;
  });

  return {
    pret,
    detruire,
    montrer() { canvas.style.visibility = 'visible'; },
    cacher() { canvas.style.visibility = 'hidden'; },
    /**
     * Secondes à AJOUTER au temps de la balise pour trouver la réplique.
     * Au cran remux la balise repart de zéro alors que le fichier est plus
     * loin (`etat.decalage`, lecteur.js) ; le réglage de synchronisation du
     * panneau s'y soustrait. Les deux bibliothèques ont le même `timeOffset`.
     */
    decaler(secondes) {
      if (instance) instance.timeOffset = Number(secondes) || 0;
    },
  };
}
